import { type Tool, tools } from "@/tools";
import { categoryOf } from "@/tools/categories";
import { scoreTerm } from "./fuzzy";

export interface ToolSearchEntry {
  tool: Tool;
  /** searchable terms: name (current locale), id words, tags, category id */
  terms: string[];
}

export interface ToolSearchResult {
  tool: Tool;
  score: number;
  category: string;
}

/**
 * Build the in-memory search index from the tool registry. Uses the tool's
 * name in the CURRENT UI locale plus its (English) id, tags and category —
 * cheap enough to rebuild per render for ~80 tools.
 */
export function buildToolIndex(): ToolSearchEntry[] {
  return tools.map((tool) => ({
    tool,
    terms: [
      tool.name(),
      tool.id.replace(/-/g, " "),
      ...tool.tags,
      categoryOf(tool.id),
    ],
  }));
}

/** Fuzzy-search the index. Returns tools scored ≥ threshold, best first. */
export function searchTools(
  index: ToolSearchEntry[],
  query: string,
  limit = 8,
  threshold = 0.35,
): ToolSearchResult[] {
  const q = query.trim();
  if (q.length < 2) return [];
  const results: ToolSearchResult[] = [];
  for (const entry of index) {
    let best = 0;
    for (const term of entry.terms) {
      const s = scoreTerm(q, term);
      if (s > best) best = s;
      if (best === 1) break;
    }
    if (best >= threshold) {
      results.push({
        tool: entry.tool,
        score: best,
        category: categoryOf(entry.tool.id),
      });
    }
  }
  results.sort(
    (a, b) => b.score - a.score || a.tool.name().localeCompare(b.tool.name()),
  );
  return results.slice(0, limit);
}
